import React, { useCallback, useEffect, useState } from "react";
import {
  BooleanField,
  Button,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ReferenceField,
  ResourceContextProvider,
  SimpleList,
  TextField,
  Title,
} from "react-admin";
import { Box, useMediaQuery } from "@mui/material";
import { GraduationServicePath, MOBILE_BREAKPOINT } from "utils/constants";
import { graduationRequests } from "api/graduationApi";
import { useNavigate, useParams } from "react-router";
import BackButtonToolbar from "features/BackButtonToolbar";
import dataProvider from "server/dataProvider";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import SecuredComponent from "server/security/SecuredComponent";
import AddIcon from "@mui/icons-material/Add";
import permissions from "app/permissions";
import DeleteStateExamDialog from "./DeleteStateExamDialog";
import UpdateStateExamDialog from "./UpdateStateExamDialog";
import { formatStudentOption } from "../../utils";
import { administrationRequests } from "api/administrationApi";

const ListStateExamsResults = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const navigate = useNavigate();
  const { id } = useParams();
  const [stateExamsResults, setStateExamsResults] = useState([]);

  const fetchStateExamsResults = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(graduationRequests.getStateExamResults(id));
      setStateExamsResults(data);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchStateExamsResults();
  }, [id, fetchStateExamsResults]);

  const isResult = stateExamsResults.some(item => Boolean(item.result));
  const isAssignedStudentsForStateExam = stateExamsResults?.length === 0;

  return (
    <>
      <Title title="Държавен изпит резултати" />
      <BackButtonToolbar />
      <Box sx={{ display: "flex", justifyContent: "end", mt: 2, mb: 1 }}>
        {!isResult && !isAssignedStudentsForStateExam && (
          <SecuredComponent permission={permissions.canManageStateExamResults}>
            <Button
              startIcon={<AddIcon />}
              size="small"
              label="Добави резултати"
              onClick={() =>
                navigate(`/${GraduationServicePath}add-state-exams-results/${id}/create`, {
                  state: { students: stateExamsResults },
                })
              }
            />
          </SecuredComponent>
        )}
      </Box>
      <ListContextProvider value={{ data: stateExamsResults }}>
        <ResourceContextProvider value={stateExamsResults}>
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <MobileListRow fields={[{ label: "Име на студент", value: formatStudentOption(false)(record) }]} />
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[{ label: "Факултетен номер", value: record?.studentInfo?.facultyNumber }]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Защита на дипломна работа?",
                        value: record?.isSkippedForThesisDefense ? "Да" : "Не",
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Държавен изпит",
                        value: (
                          <ReferenceField
                            source="result.stateExamId"
                            reference={administrationRequests.getStateExamsSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Оценка", value: record?.result?.grade }]} />
                  <MobileListRow fields={[{ label: "Точки", value: record?.result?.points }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Статус",
                        value: (
                          <ReferenceField
                            source="result.status"
                            reference={graduationRequests.getStateExamsStatusesSelect()}
                          >
                            <TextField source="nameBg" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  {isResult && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      <SecuredComponent permission={permissions.canManageStateExamResults}>
                        {record?.result && (
                          <DeleteStateExamDialog
                            studentEducationId={record?.studentEducationId}
                            stateExamId={id}
                            fetchStateExamsResults={fetchStateExamsResults}
                          />
                        )}
                        <UpdateStateExamDialog
                          studentEducationId={record?.studentEducationId}
                          stateExamId={id}
                          fetchStateExamsResults={fetchStateExamsResults}
                          defaultValues={{
                            grade: record?.result?.grade,
                            points: record?.result?.points,
                            status: record?.result?.status,
                          }}
                        />
                      </SecuredComponent>
                    </div>
                  )}
                </div>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={{
                "& .RaDatagrid-headerCell": {
                  textAlign: "left",
                },
              }}
            >
              <FunctionField
                label="Име на студент"
                render={formatStudentOption(false)}
              />
              <TextField
                source="studentInfo.facultyNumber"
                label="Факултетен номер"
              />
              <BooleanField
                source="isSkippedForThesisDefense"
                label="Защита на дипломна работа?"
              />
              <ReferenceField
                source="result.stateExamId"
                reference={administrationRequests.getStateExamsSelect()}
                label="Държавен изпит"
              >
                <TextField source="name" />
              </ReferenceField>
              <TextField
                source="result.grade"
                label="Оценка"
              />
              <TextField
                source="result.points"
                label="Точки"
              />
              <ReferenceField
                source="result.status"
                reference={graduationRequests.getStateExamsStatusesSelect()}
                label="Статус"
              >
                <TextField source="nameBg" />
              </ReferenceField>
              {isResult && (
                <FunctionField
                  render={rec => (
                    <SecuredComponent permission={permissions.canManageStateExamResults}>
                      {rec?.result && (
                        <DeleteStateExamDialog
                          studentEducationId={rec?.studentEducationId}
                          stateExamId={id}
                          fetchStateExamsResults={fetchStateExamsResults}
                        />
                      )}
                      <UpdateStateExamDialog
                        studentEducationId={rec?.studentEducationId}
                        stateExamId={id}
                        fetchStateExamsResults={fetchStateExamsResults}
                        defaultValues={{
                          grade: rec?.result?.grade,
                          points: rec?.result?.points,
                          status: rec?.result?.status,
                        }}
                      />
                    </SecuredComponent>
                  )}
                />
              )}
            </Datagrid>
          )}
        </ResourceContextProvider>
      </ListContextProvider>
    </>
  );
};

export default ListStateExamsResults;
